import { useQuery } from "@tanstack/react-query";
import React from "react";

import { QueryCacheKey } from "../../../api";
import {
  CenterAlign,
  Drawer,
  LoaderWithPerfTimings,
  Paper,
  PaperContent,
  Typography,
} from "../../../components";
import { ImportRunDetailsSidebarContent } from "../../../components/ImportRunDetailsSidebarContent";
import { getTimeZone, useApiContext } from "../../../utils";

type Props = {
  dataImportRunId: string | undefined;
  onClose: (value: string | undefined) => void;
};

export const DataImportRunDetailsDrawer: React.FC<React.PropsWithChildren<Props>> = ({
  dataImportRunId,
  onClose,
}) => {
  const { adminExtApiClient } = useApiContext();

  const {
    data: dataImportRunResponse,
    isLoading: isLoadingRun,
    error: errorRun,
  } = useQuery(
    [QueryCacheKey.ADMIN_EXT_DATA_IMPORT_RUN, dataImportRunId],
    () =>
      adminExtApiClient.getDataImportRun({
        data_import_run_id: dataImportRunId!,
        tz_name: getTimeZone(),
      }),
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: Boolean(dataImportRunId),
    }
  );

  const run = dataImportRunResponse?.data.data_import_run;

  const renderContent = () => {
    if (isLoadingRun) {
      return (
        <CenterAlign>
          <LoaderWithPerfTimings name="DataImportRunDetailsDrawer" size="large" />
        </CenterAlign>
      );
    }

    if (errorRun) {
      return (
        <CenterAlign>
          <Paper>
            <PaperContent>
              <Typography fontWeight="bold" gutterBottom>
                Error
              </Typography>
              <Typography>Unable to load import run</Typography>
            </PaperContent>
          </Paper>
        </CenterAlign>
      );
    }

    if (run === undefined) {
      return <noscript />;
    }

    return <ImportRunDetailsSidebarContent run={run} onClose={() => onClose(undefined)} />;
  };

  return (
    <Drawer
      open={dataImportRunId !== undefined}
      setOpen={open => {
        if (!open) {
          onClose(undefined);
        }
      }}
      closeOnClickAway={false}
    >
      {renderContent()}
    </Drawer>
  );
};
